import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { EncampyPortalLoginLauncher } from "components/Encampy/PortalLoginLauncher";
import { GeneratePdfButton } from "components/GeneratePdfButton";
import { CredentialFieldsDisplay } from "components/PortalCredential/CredentialFieldsDisplay";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { CredentialVerification } from "./CredentialVerification";
import { FieldDisplay } from "./shared/FieldComponents";
import { useFulfillmentDetails } from "./useFulfillmentDetails";


const VerificationSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export function StatePortal() {
  const theme = useTheme();
  const alerts = useAlerts();
  const { data } = useFulfillmentDetails();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-state-portal-expanded",
    { defaultValue: true }
  );

  const portalCredential = data?.tierIIReport.facility.portalCredential;
  const facilityAlternateIds = data?.tierIIReport.facility.facilityAlternateIds;
  const reportingYear = data?.tierIIReport.reportingYear;
  const reportKind = data?.tierIIReport.reportKind;

  const credentialsContent = (
    <CredentialFieldsDisplay
      credential={portalCredential?.credential}
      portalUrl={portalCredential?.url}
      showCombinedInvoicing={data?.tierIIReport.facility.state === "WV"}
    />
  );

  const verificationContent = portalCredential && (
    <VerificationSection>
      <CredentialVerification
        credential={portalCredential.credential}
        facilityId={data.tierIIReport.facility.id ?? ""}
      />
    </VerificationSection>
  );

  // Just the verification status field for collapsed mode
  const verificationStatusOnly = portalCredential?.credential && (
    <FieldDisplay
      label="Verification Status:"
      value={prettyPrintEnumValue(
        portalCredential.credential.verificationStatus
      )}
      noWrap
    />
  );

  const actionButtons = (
    <Stack direction="row" spacing={1} data-testid="action-buttons">
      <EncampyPortalLoginLauncher
        portalCredential={portalCredential}
        facilityAlternateIds={facilityAlternateIds}
        reportingYear={reportingYear}
        reportKind={reportKind}
        onSuccess={(message) => alerts.success(message)}
        onError={(error) => alerts.error(error)}
        size="medium"
        disabled={
          !portalCredential?.credential?.username ||
          !portalCredential?.credential?.password
        }
        disabledReason={
          !portalCredential?.credential?.username ||
          !portalCredential?.credential?.password
            ? "Portal credentials are required for portal login"
            : undefined
        }
      />
      <GeneratePdfButton
        reportId={data?.tierIIReport.id ?? ""}
        label="Download Options"
        includeTierIISubmit={true}
        reportOrgStatus={data?.tierIIReport.organizationStatus}
        disabled={!data?.tierIIReport.id?.length}
        size="small"
        endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
      />
    </Stack>
  );

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3, 2, 3)}
    >
      {/* Header with title and expand/collapse button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography variant="h6" data-testid="state-portal-title">
            State Portal
          </Typography>
          <IconButton size="small">
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Stack>

      {/* Show only verification status when collapsed */}
      {!isExpanded && verificationStatusOnly && (
        <Box sx={{ pl: 0 }}>{verificationStatusOnly}</Box>
      )}

      <Collapse in={isExpanded}>
        <Grid container spacing={3}>
          {/* Credentials content */}
          <Grid size={12}>{credentialsContent}</Grid>

          {/* Verification content */}
          <Grid size={12}>{verificationContent}</Grid>

          {/* Action buttons at bottom */}
          <Grid size={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              {actionButtons}
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </Stack>
  );
}
